import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { ERROR, SUCCESS } from 'src/app/global';
import { PatientService } from 'src/app/calvin-emr-shared/services/patient.service';
import { ToastService } from 'src/app/calvin-emr-shared/services/toast.service';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { ProviderOrder } from 'src/app/calvin-emr-shared/models/provider-order.model';
import { Vital } from 'src/app/calvin-emr-shared/models/vitals.model';

@Injectable()
export class PatientDashboardStore {
  private patientService = inject(PatientService);
  private toastService = inject(ToastService);

  readonly patient = signal<Patient>(new Patient());
  readonly collection = signal<string>('');
  readonly providerOrders = computed<ProviderOrder[]>(() => this.patient().providerOrders ?? []);
  readonly vitals = computed<Vital[]>(() => this.patient().vitals ?? []);

  private patientSub?: Subscription;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.patientSub?.unsubscribe());
  }

  setPatient(update: { patient: Patient; collection: string }): void {
    this.patient.set(update.patient);
    this.collection.set(update.collection);
  }

  setField(update: { field: string; data: any; silent?: boolean }): void {
    this.patient.update((current) => {
      if (!(update.field in current)) return current;
      const copy = Object.assign(Object.create(Object.getPrototypeOf(current)), current);
      (copy as any)[update.field] = update.data;
      return copy;
    });
    this.updatePatient({
      patient: this.patient(),
      collection: this.collection(),
      silent: update.silent,
    });
  }

  getPatient(params: { patientId: string; collection: string }): void {
    this.patientSub?.unsubscribe();
    this.patientSub = this.patientService
      .getPatient(params.patientId, params.collection)
      .subscribe({
        next: (snapshot) => {
          this.setPatient({
            patient: this.patientService.getPatientFromSnapshot(snapshot),
            collection: params.collection,
          });
        },
        error: (error) => console.error('Unable to retrieve patient:', error),
      });
  }

  updatePatient(params: { patient: Patient; collection: string; silent?: boolean }): void {
    this.patientService.upsertPatient(params.patient, params.collection).subscribe({
      next: () => {
        if (!params.silent) {
          this.toastService.addOne({
            severity: SUCCESS,
            summary: 'Success',
            detail: `Updated patient ${params.patient.getLastFirstMiddleName()}!`,
            life: 2000,
          });
        }
      },
      error: (error) =>
        this.toastService.addOne({
          severity: ERROR,
          summary: 'Error',
          detail: `Unable to update patient: ${error}`,
        }),
    });
  }
}
